// StampRenderer — abstraction seam for per-pen stamp deposit
// (architecture.md §19.10 E / §19.3 future pen extensions).
//
// Why an interface, when MVP only ships one implementation?
//
//   The 3 MVP pens (normal / water / alpha) all use the same kernel:
//   midpoint-circle pen pattern + saturating-add (or max-set) into the
//   stroke buffer. The pen-specific behaviour is just the per-stamp
//   alpha and the `StampAccumulate` mode — both forwarded to the same
//   `drawDot` / `drawLine` call.
//
//   The β release adds 3 more pens (architecture.md §19.3):
//
//     - marker  : high-alpha + saturating add (kernel reuse, parameter only)
//     - airbrush: random sparse alpha around the stamp center (NEW kernel)
//     - texture : PNG mask multiplied with the pen pattern (NEW kernel)
//
//   And Phase 11+ adds crayon / dot / blur / oil / 本物の水彩, several of
//   which need entirely new kernels (and Wasm/SIMD; see §19.12).
//
//   Plumbing each of those as a series of `if (pen === ...)` branches in
//   `StrokeRenderer` is exactly the kind of two-step expansion §19.10 E
//   asks us to avoid. Having `StampRenderer` as an interface today means
//   adding a marker pen tomorrow is one new file + one DI registration,
//   not a hot-path refactor.
//
// What it deliberately does NOT do (yet):
//
//   - It does not refactor `StrokeRenderer` to look up its `StampRenderer`
//     via DI. The MVP path is still hard-wired to the pure `drawDot` /
//     `drawLine` calls — exactly equivalent to the previous web/worker
//     code. β / Phase 11 will do the DI swap when the second pen kernel
//     actually lands.
//   - It does not introduce per-stamp polymorphic dispatch into the
//     inner stamp loop. The hot path stays monomorphic.
//
// Treat the symbols here as the forward-looking contract every new pen
// kernel implementation MUST satisfy.
import { drawDot as rasterDrawDot, drawLine as rasterDrawLine, TONE_THRESHOLD_SOFT_EDGE_WIDTH, } from '../rasterizer.js';
import { DOT_DEFAULT_SPACING, wrapWithDotSpacing } from './dotDecorator.js';
import { AIRBRUSH_DEFAULT_DENSITY, AIRBRUSH_DEFAULT_GRAIN, airbrushPeakDensityLevel, } from './texture/airbrushHash.js';
import { CRAYON_THRESHOLD_SAMPLERS, crayonGrainIndex } from './texture/crayonThreshold.js';
import { PASTEL_TEXTURE_SAMPLERS, pastelGrainIndex } from './texture/pastelTexture.js';
import { samplerForPreset } from './texture/presetTable.js';
import { TONE_THRESHOLD_SAMPLERS, toneDensityLevel, tonePitchIndex, } from './texture/toneThreshold.js';
/**
 * Default JS implementation. Thin wrappers around the pure rasterizer
 * functions — the MVP pens reuse this for normal / water / alpha (the
 * pen-specific behaviour is in the per-stamp alpha + accumulate-mode
 * choice, not in the kernel itself).
 *
 * Future pens get their own implementation (`createAirbrushStampRenderer`,
 * etc.) and `StrokeRenderer` chooses by `pen` kind.
 */
export const createJsStampRenderer = () => ({
    dot(args) {
        rasterDrawDot({
            buffer: args.buffer,
            mainRgba: args.mainRgba,
            maskSnapshot: args.maskSnapshot,
            width: args.width,
            height: args.height,
            x1: args.x,
            y1: args.y,
            penAlpha: args.penAlpha,
            penWidth: args.penWidth,
            mask: args.mask,
            accumulate: args.accumulate,
            aa: args.aa,
            widthScaleStart: args.widthScaleStart,
            widthScaleEnd: args.widthScaleEnd,
            alphaScaleStart: args.alphaScaleStart,
            alphaScaleEnd: args.alphaScaleEnd,
            clip: args.clip,
        });
    },
    line(args) {
        rasterDrawLine({
            buffer: args.buffer,
            mainRgba: args.mainRgba,
            maskSnapshot: args.maskSnapshot,
            width: args.width,
            height: args.height,
            x1: args.x1,
            y1: args.y1,
            x2: args.x2,
            y2: args.y2,
            penAlpha: args.penAlpha,
            penWidth: args.penWidth,
            mask: args.mask,
            accumulate: args.accumulate,
            aa: args.aa,
            widthScaleStart: args.widthScaleStart,
            widthScaleEnd: args.widthScaleEnd,
            alphaScaleStart: args.alphaScaleStart,
            alphaScaleEnd: args.alphaScaleEnd,
            clip: args.clip,
        });
    },
});
/**
 * Texture pen kernel (texture-brush-design.md §2). Identical to the JS
 * kernel except it injects a fixed `TextureSampler` so `stampOnce` /
 * `stampOnceAA` multiply the per-texel alpha into the per-stamp value at the
 * single quantisation point. The sampler is resolved **once per stroke** (the
 * factory captures it) so the hot path never does per-stamp polymorphic
 * dispatch — it's the same monomorphic `drawDot`/`drawLine` call with one
 * extra immutable field (StampRenderer.ts seam contract, §19.10 E).
 *
 * 横展開 (texture-brush-design.md §11 step 2): the factory now takes either a
 * `TexturePreset` id (resolved through `samplerForPreset` against the preset
 * table → mapping mode + asset + scale) OR an explicit `TextureSampler` (used
 * by tests / the UI thumbnail path). `undefined` → the spike default「紙」
 * (paper, world mapping), so `pen='texture'` strokes that omit a preset keep
 * replaying as paper (back-compat).
 *
 * The sampler is resolved ONCE here (the factory captures it); the per-stamp
 * hot loop never touches the preset table. Stamp-mapped presets (毛先/雲/
 * スプレー) rotate per stamp, but the rotation angle is derived inside
 * `drawLine` from the segment direction — the renderer just forwards the
 * sampler, so the seam stays monomorphic.
 */
export const createTextureStampRenderer = (presetOrSampler) => {
    const texture = presetOrSampler === undefined || typeof presetOrSampler === 'string'
        ? samplerForPreset(presetOrSampler)
        : presetOrSampler;
    return {
        dot(args) {
            rasterDrawDot({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x,
                y1: args.y,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                texture,
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
        line(args) {
            rasterDrawLine({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x1,
                y1: args.y1,
                x2: args.x2,
                y2: args.y2,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                texture,
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
    };
};
/**
 * Screentone pen kernel (`pen='tone'`, texture-brush-design.md §12.2). Like the
 * texture kernel it forwards a world-mapped sampler, but flips the kernel into
 * **threshold-dither mode**: the sampler is ONE of the pre-baked clustered-dot
 * threshold maps (`TONE_THRESHOLD_SAMPLERS`, selected by `pitch` —
 * phase11-light-pens-2026-08-21.md §4.5) and a texel inks iff `threshold <
 * densityLevel`, at the full per-stamp alpha. `density` (0..1 continuous番手
 * from `penParams.density`) is quantised ONCE here to the integer
 * `densityLevel` 0..256 (`toneDensityLevel`) so the kernel hot loop just
 * compares integers.
 *
 * `pitch` (`penParams.tone.pitch`, optional) selects the dot-grid pitch — a
 * PRESET SELECTION mirroring `grain` on `createCrayonStampRenderer` /
 * `createPastelStampRenderer`, not a runtime kernel parameter. `undefined` →
 * `TONE_DEFAULT_PITCH` (§4.6 wire-omitted replay fallback; see
 * `toneThreshold.ts`'s doc for why this is NOT the UI's own default).
 *
 * Resolved ONCE per stroke (the factory captures the sampler + level), so the
 * per-stamp hot loop stays monomorphic — same as the texture renderer. The
 * threshold sampler is world-mapped (penWidth 非追従, screen 固定周波数), so the
 * dot grid stays pinned to canvas coords across overlapping tone strokes AT
 * THE SAME PITCH (strokes at different pitches are independent grids, same as
 * any other world-mapped asset — no cross-pitch registration attempted).
 *
 * **AA soft edge (phase11-light-pens-2026-08-21.md §4.11, 2026-08-21)**: unlike
 * every other field here (resolved once at factory time), `textureThresholdSoft`
 * is decided PER CALL from `args.aa` — the stroke's own per-stroke AA flag,
 * forwarded on every `dot`/`line` call (`StrokeRenderer.processPoint` passes
 * `stroke.aa`, `strokeRenderer.ts:949`/`972`). When `args.aa !== true` the flag
 * stays `false`/unset, so the kernel takes the exact SAME binary-threshold
 * branch as before §4.11 — byte-identical (verified by `toneStamp.test.ts`'s
 * binary-path regression pin). When `args.aa === true`, the kernel softens
 * each halftone dot's own edge (NOT the stroke's outer silhouette — tone never
 * sets `textureThresholdEdgeFalloff`, that's crayon-only) using
 * `TONE_THRESHOLD_SOFT_EDGE_WIDTH` — a much narrower band than crayon's, since
 * §4.11 only wants "縁 1px 程度" of feathering per dot, not a gradated interior.
 */
export const createToneStampRenderer = (density, pitch) => {
    const texture = TONE_THRESHOLD_SAMPLERS[tonePitchIndex(pitch)];
    const densityLevel = toneDensityLevel(density);
    return {
        dot(args) {
            rasterDrawDot({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x,
                y1: args.y,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                texture,
                textureThreshold: true,
                densityLevel,
                // §4.11: soften each dot's own edge ONLY when this stroke is AA —
                // `args.aa` is per-call (not captured by the factory), see this
                // function's doc.
                textureThresholdSoft: args.aa === true,
                textureThresholdSoftEdgeWidth: TONE_THRESHOLD_SOFT_EDGE_WIDTH,
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
        line(args) {
            rasterDrawLine({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x1,
                y1: args.y1,
                x2: args.x2,
                y2: args.y2,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                texture,
                textureThreshold: true,
                densityLevel,
                textureThresholdSoft: args.aa === true,
                textureThresholdSoftEdgeWidth: TONE_THRESHOLD_SOFT_EDGE_WIDTH,
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
    };
};
/**
 * Airbrush pen kernel (`pen='airbrush'`, airbrush-design-2026-08-15.md §1/§2).
 * Forwards a stateless-hash + radial-falloff descriptor (`StampParams.airbrush`)
 * instead of a sampled `TextureSampler` — the kernel computes `t` from a pure
 * function of `(seed, pointIndex, sweep-step, cell)` rather than reading a
 * baked array, and compares it against a per-pixel radial density level
 * instead of tone's per-stroke constant. `density` (0..1 continuous, from
 * `penParams.density`) is quantised ONCE here to `peakDensityLevel` via
 * `airbrushPeakDensityLevel` — a dedicated non-linear curve (NOT
 * `toneDensityLevel`'s straight line; see that function's doc for why a
 * straight line makes the slider's bottom ~90% functionally dead). The
 * SAME `airbrushPeakDensityLevel` call is the single quantisation point
 * reused by `WasmStampRenderer.ts`'s `resolveAirbrushPeakDensityLevel`.
 *
 * `pointIndex` is NOT resolved here (unlike `seed`/`grain`/`peakDensityLevel`,
 * which are per-stroke constants captured once by this factory) — it varies
 * PER CALL (`dot`/`line` — different wire points get different values), so it
 * is read from `args.pointIndex` at call time (forwarded by
 * `StrokeRenderer.processPoint` from `StrokeState.pointIndex`, §1.2).
 *
 * Resolved ONCE per stroke (the factory captures seed/grain/peakDensityLevel),
 * so the per-stamp hot loop stays monomorphic — same shape as
 * `createTextureStampRenderer` / `createToneStampRenderer`.
 */
export const createAirbrushStampRenderer = (seed, grain, density) => {
    const peakDensityLevel = airbrushPeakDensityLevel(density);
    return {
        dot(args) {
            rasterDrawDot({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x,
                y1: args.y,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                airbrush: { seed, grain, peakDensityLevel, pointIndex: args.pointIndex ?? 0 },
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
        line(args) {
            rasterDrawLine({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x1,
                y1: args.y1,
                x2: args.x2,
                y2: args.y2,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                airbrush: { seed, grain, peakDensityLevel, pointIndex: args.pointIndex ?? 0 },
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
    };
};
/**
 * Crayon pen kernel (`pen='crayon'`, phase11-light-pens-2026-08-21.md §2).
 * Rides the SAME threshold-dither seam `createToneStampRenderer` uses
 * (`textureThreshold: true`) but samples one of the 3 pre-baked wax-grain
 * maps (`crayonThreshold.ts`) instead of tone's clustered-dot halftone cell.
 *
 * **Density level fallback (§2.4)**: the design's primary proposal links the
 * threshold comparison to the PER-STAMP `penAlpha` (so coverage moves with
 * pressure within one stroke). That requires a new per-stamp field threaded
 * through the shared kernel (`stampOnce`/`stampOnceAA` in rasterizer.ts) AND
 * its Rust mirror — a change to the hot path every other pen also runs
 * through, whose visual correctness can only really be judged by an actual
 * device/browser session. Per §2.4's own explicit permission to fall back
 * when the primary approach "実装が膨らむ", this implementation takes the
 * FALLBACK instead: `densityLevel` is a STROKE-LEVEL constant derived once
 * from the stroke's own `alpha` (0..255, the user's alpha slider value) via
 * the SAME `toneDensityLevel` quantisation `tone` uses — zero new kernel
 * fields, zero Rust changes beyond registering the map. This gives "薄く
 * 設定するとかすれる" (thinner alpha ⇒ sparser coverage) rather than "強く
 * 押すと被覆が動く" (pressure-driven coverage within a single stroke) — see
 * this package's Phase 11 implementation report for the full record of this
 * decision.
 *
 * Resolved ONCE per stroke (the factory captures the sampler + level), same
 * shape as `createToneStampRenderer` / `createTextureStampRenderer`.
 *
 * **Grain quality (§2.5.1)**: the sampled map (`crayonThreshold.ts`) is now a
 * multi-octave FRACTAL noise stack rather than a single-scale cell hash (see
 * that file's history section for the two failed single-scale designs), and
 * this renderer sets `textureThresholdSoft: true` — a crayon-only kernel
 * branch (`StampParams.textureThresholdSoft`'s doc) that softens the ink
 * alpha near the threshold cutoff instead of the hard binary edge `tone`
 * keeps. Both changes address the same real-device feedback ("荒い、クレヨン
 * っぽくない") without touching `tone`'s output (parity-tested).
 */
export const createCrayonStampRenderer = (grain, strokeAlpha) => {
    const texture = CRAYON_THRESHOLD_SAMPLERS[crayonGrainIndex(grain)];
    const densityLevel = toneDensityLevel(strokeAlpha / 255);
    return {
        dot(args) {
            rasterDrawDot({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x,
                y1: args.y,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                texture,
                textureThreshold: true,
                densityLevel,
                // §2.5.1: soft threshold edge — softens the ink/no-ink cutoff at the
                // grain boundary instead of the hard binary edge `tone` (with
                // `aa=false`) uses. See `StampParams.textureThresholdSoft`'s doc for
                // the exact formula + the tone-parity guarantee. Width is omitted →
                // defaults to `CRAYON_THRESHOLD_SOFT_EDGE_WIDTH` (crayon's own,
                // unaffected by tone's narrower §4.11 width).
                textureThresholdSoft: true,
                // §2.5.2: crayon-only radial edge falloff — frays the STROKE's outer
                // silhouette (not just each grain's own edge). Tone never sets this,
                // even for its own §4.11 AA soft edge.
                textureThresholdEdgeFalloff: true,
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
        line(args) {
            rasterDrawLine({
                buffer: args.buffer,
                mainRgba: args.mainRgba,
                maskSnapshot: args.maskSnapshot,
                width: args.width,
                height: args.height,
                x1: args.x1,
                y1: args.y1,
                x2: args.x2,
                y2: args.y2,
                penAlpha: args.penAlpha,
                penWidth: args.penWidth,
                mask: args.mask,
                accumulate: args.accumulate,
                aa: args.aa,
                texture,
                textureThreshold: true,
                densityLevel,
                textureThresholdSoft: true,
                textureThresholdEdgeFalloff: true,
                widthScaleStart: args.widthScaleStart,
                widthScaleEnd: args.widthScaleEnd,
                alphaScaleStart: args.alphaScaleStart,
                alphaScaleEnd: args.alphaScaleEnd,
                clip: args.clip,
            });
        },
    };
};
/**
 * Pastel pen kernel (`pen='pastel'`, phase11-light-pens-2026-08-21.md §2 /
 * §2.5.5).
 *
 * **Revised 2026-08-21 (§2.5.5, "build-up を取り戻す")**: unlike every other
 * texture-seam renderer in this file, pastel's grain is **NOT** multiplied
 * into the per-dab kernel anymore. The per-dab stamp is now the plain JS
 * kernel (byte-for-byte `createJsStampRenderer`'s shape — no `texture` field
 * forwarded to `rasterDrawDot`/`rasterDrawLine`). The grain multiply moved to
 * the COMMIT step (`commitBufferToLayer`/`commitBufferToSubrect`'s
 * `commitTexture` param, resolved by `resolveCommitTexture` below and wired
 * in by `strokeRenderer.ts`'s `StrokeState.commitTexture`).
 *
 * Why: §2.5.4's flat/max cadence made the grain visible but killed 1-stroke
 * build-up (同じ場所を往復しても濃くならない). The author wants BOTH — build-up
 * AND grain. Moving the texture multiply to commit means it runs exactly
 * ONCE per stroke (not once per dab, ~2r times), so it can't compress the
 * texel-to-texel contrast the way per-dab `accumulate='add'` did (§2.3's
 * `1-(1-s·t)^N` diagnosis — N=1 at commit, so no compounding). The buffer
 * itself goes back to plain `accumulate='add'` build-up
 * (`strokeRenderer.ts`'s `accumulateModeFor`), so overlapping dabs within one
 * stroke build up toward full opacity like `brush`/`texture` — the grain is
 * then stamped on top of that build-up ONCE at commit.
 *
 * This factory is now trivial (no texture forwarding) — kept as its own named
 * function (rather than aliasing `createJsStampRenderer()` directly in
 * `resolveStampRenderer`) purely for symmetry/documentation with the other
 * pen kernel factories in this file and as a stable seam if pastel's per-dab
 * kernel ever needs its own behaviour again.
 *
 * Cadence (unchanged by §2.5.5): pastel is STILL a deferred-commit pen
 * (`strokeRenderer.ts`'s `isFlatPen` predicate — one commit at stroke-end,
 * not per-segment); only the ACCUMULATE mode flipped from `'max'` to `'add'`.
 * See `accumulateModeFor`'s doc for why these two concerns are now resolved
 * independently instead of both hanging off the same `isFlatPen` boolean.
 *
 * Resolved ONCE per stroke, same shape as every other kernel factory here.
 */
export const createPastelStampRenderer = () => createJsStampRenderer();
/**
 * Resolve the commit-time texture sampler for a stroke, or `undefined` when
 * this pen has no commit-time texture axis (every pen except `pastel` today —
 * phase11-light-pens-2026-08-21.md §2.5.5). `undefined` means
 * `commitBufferToLayer`/`commitBufferToSubrect` skip the texture-multiply
 * branch entirely and behave byte-for-byte as before §2.5.5.
 *
 * Deliberately generic (keyed on `pen`, not hardcoded to pastel at the call
 * site) so a future pen that wants the same "grain applied once at commit"
 * mechanism only needs a new branch here — `strokeRenderer.ts` and
 * `rasterizer.ts`'s commit path stay unchanged (§19.10 E seam posture).
 *
 * `grain` selection mirrors `createPastelStampRenderer`'s old per-dab
 * resolution exactly (`pastelGrainIndex`) — the SAME 3 pre-baked maps, just
 * sampled at a different point in the pipeline now.
 */
export const resolveCommitTexture = (pen, penParams) => {
    if (pen !== 'pastel')
        return undefined;
    const grain = penParams?.kind === 'pastel' ? penParams.grain : undefined;
    return PASTEL_TEXTURE_SAMPLERS[pastelGrainIndex(grain)];
};
/**
 * No-op renderer: `dot`/`line` do nothing. Used ONLY as the defensive
 * fallback for a `pen='airbrush'` stroke with no usable seed
 * (airbrush-design-2026-08-15.md §4-3 — "seed 欠落時は fallback せず reject
 * (または描かない)"). The wire-level reject is `StrokeService.handleStart`'s
 * job (`@oekaki/server`); this is the belt-and-braces path so a malformed or
 * legacy-recorded event can't crash a replay — it just draws nothing instead
 * of fabricating a seed that would make every such stroke render identically
 * for every sender (the exact failure mode the reject-not-fallback design
 * choice exists to avoid).
 */
const createNoopStampRenderer = () => ({
    dot: () => {
        // intentionally empty
    },
    line: () => {
        // intentionally empty
    },
});
/**
 * DI: pick the `StampRenderer` implementation for a pen kind
 * (texture-brush-design.md §5 — the forcing function). `StrokeRenderer`
 * calls this **once per stroke** (in `startStroke`) and stores the result
 * on the stroke state, so the per-stamp hot loop stays monomorphic.
 *
 * - `texture` → `createTextureStampRenderer(preset)`. The preset comes from
 *   the `stroke.start` `penParams` (`{ kind:'texture', preset }`,
 *   texture-brush-design.md §4 案B / §11). When `penParams` is omitted (or is
 *   not a `texture` arm), the renderer falls back to the「紙」default — keeping
 *   spike-era recorded strokes (which carry no preset) replaying as paper.
 * - `tone` → `createToneStampRenderer(density, pitch)`. `pitch` (phase11-
 *   light-pens-2026-08-21.md §4.5/§4.6) selects one of the pre-baked
 *   `TONE_THRESHOLD_SAMPLERS`; a missing/mismatched `penParams` OR an
 *   omitted `pitch` field falls back to `TONE_DEFAULT_PITCH` (the OLD
 *   hardcoded cell size — replay fidelity for pre-pitch strokes, §4.6).
 * - `airbrush` → `createAirbrushStampRenderer(seed, grain, density)`
 *   (airbrush-design-2026-08-15.md §4-3). A missing/mismatched `penParams`
 *   has NO valid fallback (unlike texture's 紙 default) — synthesizing a seed
 *   would make every such stroke render identically for every sender. This
 *   resolves to a no-op renderer instead (see `createNoopStampRenderer`); the
 *   wire-level reject is `StrokeService.handleStart`'s job.
 * - `crayon` → `createCrayonStampRenderer(grain, alpha)` (phase11-light-pens-
 *   2026-08-21.md §2.6). `grain` selects one of 3 pre-baked wax-grain maps;
 *   `alpha` (the stroke's own wire alpha, REQUIRED here unlike every other
 *   branch) drives the density-level fallback — see that factory's doc.
 * - `pastel` → `createPastelStampRenderer()` (phase11-light-pens-2026-08-21.md
 *   §2.5.5, revised 2026-08-21). The per-dab kernel no longer forwards a
 *   texture — pastel's grain is applied ONCE at commit instead (see that
 *   factory's doc + `resolveCommitTexture`, which `strokeRenderer.ts` calls
 *   separately to resolve the SAME `grain` into the commit-time sampler).
 * - `dot` → `wrapWithDotSpacing(createJsStampRenderer(), spacing)` (same doc
 *   §3). The stamps themselves are plain brush stamps (no texture/threshold)
 *   — only the CADENCE of `dot()` calls differs, via the decorator. A
 *   missing/mismatched `penParams` falls back to `DOT_DEFAULT_SPACING`
 *   (unlike airbrush's seed, a default spacing has no fairness/determinism
 *   downside — see `dotDecorator.ts`'s doc).
 * - everything else (brush / flat / marker / eraser*) → `createJsStampRenderer()`.
 *
 * (*eraser never reaches the StampRenderer — it's destination-out via
 * `eraseLine`/`eraseDot` — but resolving a renderer for it is harmless.)
 *
 * Existing pens get the JS kernel (= byte-for-byte unchanged), which is the
 * spike's hard invariant. The SAME resolve runs on the web `EngineCore` and
 * the server `SnapshotEngine`, so both pick the identical sampler → the
 * texture stroke is byte-identical (texture-brush-design.md §4 server replay).
 *
 * `alpha` is optional (defaults to 255) because only the `crayon` branch
 * reads it — every pre-existing call site that doesn't care about crayon can
 * keep calling this with 2 args, byte-for-byte unchanged.
 */
export const resolveStampRenderer = (pen, penParams, alpha = 255) => {
    if (pen === 'texture') {
        const preset = penParams?.kind === 'texture' ? penParams.preset : undefined;
        return createTextureStampRenderer(preset);
    }
    if (pen === 'tone') {
        // density (連続番手) from penParams; missing/non-tone arm → 0.5 (mid番手) as a
        // safe default so a tone stroke without params still renders a visible tone.
        const density = penParams?.kind === 'tone' ? penParams.density : 0.5;
        // pitch (phase11-light-pens-2026-08-21.md §4.5/§4.6): missing/non-tone
        // arm OR an omitted `pitch` field on a well-formed tone arm both resolve
        // through `createToneStampRenderer`'s own `tonePitchIndex(undefined)` →
        // `TONE_DEFAULT_PITCH` fallback (replay fidelity for pre-pitch strokes).
        const pitch = penParams?.kind === 'tone' ? penParams.pitch : undefined;
        return createToneStampRenderer(density, pitch);
    }
    if (pen === 'airbrush') {
        const params = penParams?.kind === 'airbrush' ? penParams : undefined;
        if (params === undefined) {
            return createNoopStampRenderer();
        }
        const grain = params.grain ?? AIRBRUSH_DEFAULT_GRAIN;
        const density = params.density ?? AIRBRUSH_DEFAULT_DENSITY;
        return createAirbrushStampRenderer(params.seed, grain, density);
    }
    if (pen === 'crayon') {
        const grain = penParams?.kind === 'crayon' ? penParams.grain : undefined;
        return createCrayonStampRenderer(grain, alpha);
    }
    if (pen === 'pastel') {
        // §2.5.5: no per-dab texture anymore — the grain is applied once at
        // commit via `resolveCommitTexture` (called separately by
        // `strokeRenderer.ts`, which owns the commit path). The per-dab kernel
        // is just the plain JS renderer.
        return createPastelStampRenderer();
    }
    if (pen === 'dot') {
        const spacing = penParams?.kind === 'dot' ? penParams.spacing : DOT_DEFAULT_SPACING;
        return wrapWithDotSpacing(createJsStampRenderer(), spacing);
    }
    return createJsStampRenderer();
};
//# sourceMappingURL=StampRenderer.js.map